import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import {
	EnvelopeIcon,
	KeyIcon,
	GlobeAltIcon,
	CloudIcon,
	DocumentTextIcon,
	CheckCircleIcon,
	CurrencyDollarIcon,
	LinkIcon,
	Cog6ToothIcon,
	CpuChipIcon,
	ArrowPathIcon,
	BeakerIcon,
	ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

import { API_URL } from '../../config/api';
import { contentApi } from '../../api/content';

const SystemSettings = () => {
	const location = useLocation();
	const [settings, setSettings] = useState({
		email_host: '',
		email_port: '',
		email_use_tls: true,
		email_host_user: '',
		email_host_password: '',
		default_from_email: '',
		n8n_webhook_url: '',
		n8n_api_token: '',
		google_vision_enabled: false
	});
	const [googleVisionFile, setGoogleVisionFile] = useState(null);
	const [googleVisionFilePreview, setGoogleVisionFilePreview] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState(false);
	
	// Estado para links de formularios de servicios (ahora con tipo de acción)
	const [serviceForms, setServiceForms] = useState({
		form_url_srl_eirl: { type: 'form', value: '' },
		form_url_persona_fisica: { type: 'form', value: '' },
		form_url_ong: { type: 'form', value: '' },
		form_url_empresas_carpeta: { type: 'form', value: '' },
		form_url_outsourcing_igualas: { type: 'form', value: '' },
		form_url_general: { type: 'form', value: '' },
		form_url_constitucion: { type: 'form', value: '' },
		form_url_gestiones_legales: { type: 'form', value: '' },
		form_url_renovacion_mercantil: { type: 'form', value: '' },
		form_url_asesoria_fiscal: { type: 'form', value: '' },
		form_url_estados_financieros: { type: 'form', value: '' },
		form_url_auditorias: { type: 'form', value: '' },
		form_url_inventarios: { type: 'form', value: '' },
		form_url_imagen_corporativa: { type: 'form', value: '' },
		form_url_diseno_logo: { type: 'form', value: '' },
		form_url_elaboracion_sellos: { type: 'form', value: '' },
		form_url_asesoria_financiera: { type: 'form', value: '' },
		form_url_redes_sociales: { type: 'form', value: '' },
		form_url_pautas_tv: { type: 'form', value: '' },
		form_url_posicionamiento_marca: { type: 'form', value: '' },
		form_url_centros_salud: { type: 'form', value: '' },
		form_url_registro_marca: { type: 'form', value: '' },
		form_url_facturacion_electronica: { type: 'form', value: '' },
	});
	const [loadingForms, setLoadingForms] = useState(true);
	const [savingForms, setSavingForms] = useState(false);
	
	// Estado para precios de planes
	const [planPrices, setPlanPrices] = useState({
		currency: 'RD$',
		ong: {},
		srl: {},
		eirl: {},
		persona_fisica: {}
	});
	const [loadingPlanPrices, setLoadingPlanPrices] = useState(true);
	const [savingPlanPrices, setSavingPlanPrices] = useState(false);
	
	// Estado para pestañas
	const [activeTab, setActiveTab] = useState('services');

	// ── Estado LLM ──
	const LLM_PROVIDERS = [
		{
			value: 'openai',
			label: 'OpenAI (GPT)',
			models: [
				{ id: 'gpt-5.4',              label: 'GPT-5.4 — última generación ⭐' },
				{ id: 'gpt-5.3-chat-latest', label: 'GPT-5.3 Chat (latest) — conversacional ⭐' },
				{ id: 'gpt-4.1',              label: 'GPT-4.1 — última generación ⭐ (nuevo)' },
				{ id: 'gpt-4.1-mini',         label: 'GPT-4.1 Mini — rápido y económico ⭐ (nuevo)' },
				{ id: 'gpt-4.1-nano',         label: 'GPT-4.1 Nano — ultra económico (nuevo)' },
				{ id: 'gpt-4o',               label: 'GPT-4o — alta calidad' },
				{ id: 'gpt-4o-mini',          label: 'GPT-4o Mini — económico' },
				{ id: 'o4-mini',              label: 'o4-mini — razonamiento rápido (nuevo)' },
				{ id: 'o3',                   label: 'o3 — razonamiento máximo' },
				{ id: 'o3-mini',              label: 'o3-mini — razonamiento avanzado' },
				{ id: 'o1',                   label: 'o1 — razonamiento avanzado' },
				{ id: 'o1-mini',              label: 'o1-mini — razonamiento ligero' },
				{ id: 'gpt-4-turbo',          label: 'GPT-4 Turbo' },
				{ id: 'gpt-3.5-turbo',        label: 'GPT-3.5 Turbo — muy barato' },
			],
		},
		{
			value: 'anthropic',
			label: 'Anthropic (Claude)',
			models: [
				{ id: 'claude-opus-4-5',             label: 'Claude Opus 4.5 — máxima calidad ⭐ (nuevo)' },
				{ id: 'claude-sonnet-4-5',           label: 'Claude Sonnet 4.5 — equilibrado ⭐ (nuevo)' },
				{ id: 'claude-haiku-4-5',            label: 'Claude Haiku 4.5 — rápido y barato (nuevo)' },
				{ id: 'claude-opus-4-0',             label: 'Claude Opus 4 — alta calidad' },
				{ id: 'claude-3-7-sonnet-20250219',  label: 'Claude 3.7 Sonnet' },
				{ id: 'claude-3-5-haiku-20241022',   label: 'Claude 3.5 Haiku — económico' },
				{ id: 'claude-3-5-sonnet-20241022',  label: 'Claude 3.5 Sonnet' },
				{ id: 'claude-3-opus-20240229',      label: 'Claude 3 Opus' },
			],
		},
		{
			value: 'gemini',
			label: 'Google Gemini',
			models: [
				{ id: 'gemini-2.5-pro',          label: 'Gemini 2.5 Pro — máxima calidad ⭐ (nuevo)' },
				{ id: 'gemini-2.5-flash',        label: 'Gemini 2.5 Flash — rápido y barato ⭐ (nuevo)' },
				{ id: 'gemini-2.0-flash',        label: 'Gemini 2.0 Flash — equilibrado' },
				{ id: 'gemini-2.0-flash-lite',   label: 'Gemini 2.0 Flash Lite — ultra económico' },
				{ id: 'gemini-1.5-flash-002',    label: 'Gemini 1.5 Flash' },
				{ id: 'gemini-1.5-pro-002',      label: 'Gemini 1.5 Pro — alta calidad' },
			],
		},
		{
			value: 'groq',
			label: 'Groq (ultra rápido, gratis)',
			models: [
				{ id: 'llama-3.3-70b-versatile',           label: 'Llama 3.3 70B — mejor calidad ⭐' },
				{ id: 'llama-3.1-70b-versatile',           label: 'Llama 3.1 70B — alta calidad' },
				{ id: 'llama-3.1-8b-instant',              label: 'Llama 3.1 8B — ultra rápido' },
				{ id: 'deepseek-r1-distill-llama-70b',     label: 'DeepSeek R1 Llama 70B — razonamiento' },
				{ id: 'deepseek-r1-distill-qwen-32b',      label: 'DeepSeek R1 Qwen 32B — razonamiento' },
				{ id: 'qwen-qwq-32b',                      label: 'Qwen QwQ 32B — razonamiento' },
				{ id: 'mixtral-8x7b-32768',                label: 'Mixtral 8x7B' },
				{ id: 'gemma2-9b-it',                      label: 'Gemma2 9B' },
			],
		},
	];

	// Estado global LLM (modo y prompts son compartidos)
	const [llmConfig, setLlmConfig] = useState({
		llm_modo_procesamiento: 'n8n',
		llm_provider: 'openai',
		llm_prompt_606: '',
		llm_prompt_607: '',
	});

	// Estado por proveedor: api_key y model se guardan independientemente
	const DEFAULT_PROVIDER_MODELS = {
		openai:    'gpt-4o-mini',
		anthropic: 'claude-3-5-haiku-20241022',
		gemini:    'gemini-2.0-flash',
		groq:      'llama-3.3-70b-versatile',
	};
	const [llmProviderConfigs, setLlmProviderConfigs] = useState({
		openai:    { api_key: '', model: 'gpt-4o-mini',                  apiKeyChanged: false },
		anthropic: { api_key: '', model: 'claude-3-5-haiku-20241022',    apiKeyChanged: false },
		gemini:    { api_key: '', model: 'gemini-2.0-flash',             apiKeyChanged: false },
		groq:      { api_key: '', model: 'llama-3.3-70b-versatile',      apiKeyChanged: false },
	});

	const [loadingLlm, setLoadingLlm] = useState(false);
	const [savingLlm, setSavingLlm] = useState(false);
	const [testingLlm, setTestingLlm] = useState(false);
	const [llmTestResult, setLlmTestResult] = useState(null);
	const [llmError, setLlmError] = useState('');
	const [llmSuccess, setLlmSuccess] = useState(false);
	const [activePromptTab, setActivePromptTab] = useState('606');
	const [llmPendingChanges, setLlmPendingChanges] = useState(false);

	// Helpers para acceder a la config del proveedor activo
	const activeProvider = llmConfig.llm_provider || 'openai';
	const activeProviderCfg = llmProviderConfigs[activeProvider] || { api_key: '', model: DEFAULT_PROVIDER_MODELS[activeProvider] || '', apiKeyChanged: false };

	const setActiveProviderField = (field, value) => {
		setLlmProviderConfigs(prev => ({
			...prev,
			[activeProvider]: { ...prev[activeProvider], [field]: value },
		}));
		setLlmPendingChanges(true);
	};

	useEffect(() => {
		const loadAllData = async () => {
			try {
				// Cargar todos los datos en paralelo
				await Promise.allSettled([
					loadGoogleVisionCredentials(),
					loadServiceForms(),
					loadPlanPrices(),
					loadLlmConfig(),
				]);
			} catch (error) {
				console.error('Error cargando datos iniciales:', error);
			} finally {
				// Siempre marcar como cargado, incluso si hay errores
				setLoading(false);
			}
		};
		
		loadAllData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Scroll a la sección LLM cuando se navega con hash #llm-procesamiento
	useEffect(() => {
		if (location.hash === '#llm-procesamiento') {
			const el = document.getElementById('llm-procesamiento');
			if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}, [location.hash]);

	const loadServiceForms = async () => {
		try {
			const forms = await contentApi.getServiceForms();
			// Asegurar que todos los campos tengan el formato correcto {type, value}
			const normalizedForms = {};
			const initialKeys = Object.keys(serviceForms);
			initialKeys.forEach(key => {
				if (forms[key]) {
					// Si viene en el nuevo formato (objeto con type y value)
					if (typeof forms[key] === 'object' && forms[key].type && forms[key].value !== undefined) {
						normalizedForms[key] = forms[key];
					} else {
						// Si viene en formato legacy (solo string), convertir
						normalizedForms[key] = {
							type: 'form',
							value: forms[key] || ''
						};
					}
				} else {
					normalizedForms[key] = { type: 'form', value: '' };
				}
			});
			setServiceForms(normalizedForms);
		} catch (err) {
			console.error('Error cargando links de formularios:', err);
		} finally {
			setLoadingForms(false);
		}
	};

	const handleSaveServiceForms = async (e) => {
		e.preventDefault();
		setSavingForms(true);
		setError('');
		setSuccess(false);

		try {
			// Enviar TODOS los campos, incluso los vacíos
			// El backend decidirá cuáles actualizar
			const formsToSend = {};
			Object.keys(serviceForms).forEach(key => {
				const formData = serviceForms[key];
				if (formData) {
					formsToSend[key] = {
						type: formData.type || 'form',
						value: (formData.value || '').trim()
					};
				}
			});

			const response = await contentApi.updateServiceForms(formsToSend);
			
			if (response.errors && response.errors.length > 0) {
				// Si hay errores pero también se actualizó algo
				setError(`Se guardaron algunos cambios, pero hubo errores: ${response.errors.join(', ')}`);
			} else {
				setSuccess(true);
				setTimeout(() => setSuccess(false), 3000);
			}
		} catch (err) {
			// Mostrar errores específicos si están disponibles
			let errorMessage = '';
			if (err.response?.data?.errors && Array.isArray(err.response.data.errors) && err.response.data.errors.length > 0) {
				errorMessage = `Errores: ${err.response.data.errors.join(', ')}`;
			} else if (err.response?.data?.detail) {
				errorMessage = err.response.data.detail;
			} else if (err.response?.data?.message) {
				errorMessage = err.response.data.message;
			} else if (err.message) {
				errorMessage = err.message;
			} else {
				errorMessage = 'Error al guardar links de formularios';
			}
			
			console.error('Error guardando service forms:', errorMessage);
			setError(errorMessage);
		} finally {
			setSavingForms(false);
		}
	};

	const loadPlanPrices = async () => {
		try {
			const prices = await contentApi.getPlanPrices();
			if (prices) {
				setPlanPrices(prices);
			}
		} catch (err) {
			console.error('Error cargando precios de planes:', err);
		} finally {
			setLoadingPlanPrices(false);
		}
	};

	const handleSavePlanPrices = async (e) => {
		e.preventDefault();
		setSavingPlanPrices(true);
		setError('');
		setSuccess(false);

		try {
			await contentApi.updatePlanPrices(planPrices);
			setSuccess(true);
			setTimeout(() => setSuccess(false), 3000);
		} catch (err) {
			setError(err.response?.data?.detail || 'Error al guardar precios de planes');
		} finally {
			setSavingPlanPrices(false);
		}
	};

	const updatePlanPrice = (planType, planKey, field, value) => {
		const numValue = parseInt(value) || 0;
		setPlanPrices(prev => ({
			...prev,
			[planType]: {
				...prev[planType],
				[planKey]: {
					...prev[planType][planKey],
					[field]: numValue
				}
			}
		}));
	};

	const loadGoogleVisionCredentials = async () => {
		try {
			const response = await axios.get(`${API_URL}/api/content/system-config/google_vision_credentials/`, {
				timeout: 10000 // 10 segundos de timeout
			});
			if (response.data && response.data.file_url) {
				setGoogleVisionFilePreview(response.data.file_url);
			}
		} catch (err) {
			// No hay credenciales configuradas aún o hay un error - esto es normal
			if (err.response?.status !== 404 && err.response?.status !== 500) {
				console.error('Error cargando credenciales de Google Vision:', err);
			}
		}
	};

	const handleGoogleVisionFileChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			if (!file.name.endsWith('.json')) {
				setError('El archivo debe ser un JSON');
				return;
			}
			setGoogleVisionFile(file);
			// Leer y mostrar preview del contenido JSON
			const reader = new FileReader();
			reader.onload = (event) => {
				try {
					const jsonContent = JSON.parse(event.target.result);
					setGoogleVisionFilePreview(JSON.stringify(jsonContent, null, 2));
				} catch (err) {
					setError('El archivo JSON no es válido');
				}
			};
			reader.readAsText(file);
		}
	};

	const handleUploadGoogleVision = async () => {
		if (!googleVisionFile) {
			setError('Selecciona un archivo JSON');
			return;
		}

		setSaving(true);
		setError('');
		setSuccess(false);

		try {
			const formData = new FormData();
			formData.append('file', googleVisionFile);

			await axios.post(
				`${API_URL}/api/content/system-config/upload_google_vision_credentials/`,
				formData,
				{
					headers: { 'Content-Type': 'multipart/form-data' }
				}
			);

			setSuccess(true);
			setTimeout(() => setSuccess(false), 3000);
		} catch (err) {
			setError(err.response?.data?.detail || 'Error al subir el archivo');
		} finally {
			setSaving(false);
		}
	};

	const handleSave = async (e) => {
		e.preventDefault();
		setError('');
		setSuccess(false);
		setSaving(true);

		try {
			// En una implementación real, esto guardaría en el backend
			// Por ahora, solo simulamos el guardado
			await new Promise(resolve => setTimeout(resolve, 1000));
			setSuccess(true);
			setTimeout(() => setSuccess(false), 3000);
		} catch (err) {
			setError('Error al guardar configuración');
		} finally {
			setSaving(false);
		}
	};

	// ── Handlers LLM ──
	const loadLlmConfig = async () => {
		setLoadingLlm(true);
		try {
			const res = await axios.get(`${API_URL}/api/content/system-config/llm_config/`);
			const data = res.data;

			// Cargar configuración global
			setLlmConfig(prev => ({
				...prev,
				llm_modo_procesamiento: data.llm_modo_procesamiento || 'n8n',
				llm_provider: data.llm_provider || 'openai',
				llm_prompt_606: data.llm_prompt_606 || prev.llm_prompt_606,
				llm_prompt_607: data.llm_prompt_607 || prev.llm_prompt_607,
			}));

			// Cargar config por proveedor si el backend la devuelve
			if (data.provider_configs) {
				setLlmProviderConfigs(prev => {
					const updated = { ...prev };
					Object.entries(data.provider_configs).forEach(([p, cfg]) => {
						if (updated[p]) {
							updated[p] = {
								...updated[p],
								api_key: cfg.api_key || '',
								model: cfg.model || DEFAULT_PROVIDER_MODELS[p] || '',
								apiKeyChanged: false,
							};
						}
					});
					return updated;
				});
			}
		} catch (err) {
			console.error('Error cargando config LLM:', err);
		} finally {
			setLoadingLlm(false);
		}
	};

	const loadDefaultPrompts = async () => {
		try {
			const res = await axios.get(`${API_URL}/api/content/system-config/default_prompts/`);
			setLlmConfig(prev => ({
				...prev,
				llm_prompt_606: res.data.prompt_606,
				llm_prompt_607: res.data.prompt_607,
			}));
		} catch (err) {
			console.error('Error cargando prompts por defecto:', err);
		}
	};

	// Auto-guarda solo el modelo cuando el usuario lo cambia (sin api_key)
	const autoSaveModel = async (provider, model) => {
		try {
			const payload = {
				llm_provider: provider,
				[`llm_model_${provider}`]: model,
			};
			await axios.post(`${API_URL}/api/content/system-config/save_llm_config/`, payload);
			// Sin feedback visual para no interrumpir el flujo
		} catch (err) {
			console.warn('Auto-save del modelo falló:', err);
		}
	};

	const handleSaveLlm = async () => {
		setSavingLlm(true);
		setLlmError('');
		setLlmSuccess(false);
		try {
			// Construir payload con config global + config por proveedor
			const payload = {
				llm_modo_procesamiento: llmConfig.llm_modo_procesamiento,
				llm_provider: llmConfig.llm_provider,
				llm_prompt_606: llmConfig.llm_prompt_606,
				llm_prompt_607: llmConfig.llm_prompt_607,
			};

			// Guardar api_key y model de cada proveedor
			Object.entries(llmProviderConfigs).forEach(([p, cfg]) => {
				// Solo incluir la api_key si fue modificada (evitar enviar valor enmascarado)
				if (cfg.apiKeyChanged && cfg.api_key && !cfg.api_key.split('').every(c => c === '•' || c === '*')) {
					payload[`llm_api_key_${p}`] = cfg.api_key;
				}
				if (cfg.model) {
					payload[`llm_model_${p}`] = cfg.model;
				}
			});

			await axios.post(`${API_URL}/api/content/system-config/save_llm_config/`, payload);

			// Marcar todas las api_keys como no-cambiadas tras guardar
			setLlmProviderConfigs(prev => {
				const updated = { ...prev };
				Object.keys(updated).forEach(p => {
					updated[p] = { ...updated[p], apiKeyChanged: false };
				});
				return updated;
			});

			setLlmPendingChanges(false);
			setLlmSuccess(true);
			setTimeout(() => setLlmSuccess(false), 3000);
		} catch (err) {
			setLlmError(err.response?.data?.detail || err.message || 'Error al guardar configuración LLM');
		} finally {
			setSavingLlm(false);
		}
	};

	const handleTestLlm = async () => {
		setTestingLlm(true);
		setLlmTestResult(null);
		setLlmError('');
		try {
			const provider = llmConfig.llm_provider;
			const provCfg = llmProviderConfigs[provider] || {};

			const testPayload = {
				llm_provider: provider,
				llm_model: provCfg.model || activeProviderCfg.model,
				llm_prompt_606: llmConfig.llm_prompt_606,
			};

		// Incluir api_key SOLO si fue modificada manualmente y no está enmascarada.
		// Si NO fue cambiada (apiKeyChanged=false), el backend usará la guardada en DB.
		// NUNCA enviar el valor enmascarado con bullets (•) porque causa UnicodeEncodeError.
		const apiKey = provCfg.api_key || '';
		const apiKeyIsMasked = !apiKey || apiKey.split('').every(c => c === '•' || c === '*');
		if (provCfg.apiKeyChanged && apiKey && !apiKeyIsMasked) {
			testPayload.llm_api_key = apiKey;
			testPayload[`llm_api_key_${provider}`] = apiKey;
		}
		// Si la key no cambió o está enmascarada → NO incluirla → backend usa DB

			const res = await axios.post(
				`${API_URL}/api/content/system-config/test_llm_connection/`,
				testPayload
			);
			setLlmTestResult(res.data);
		} catch (err) {
			setLlmTestResult({
				success: false,
				error: err.response?.data?.error || err.message || 'Error de red al probar conexión',
			});
		} finally {
			setTestingLlm(false);
		}
	};

	const tabs = [
		{ id: 'services', name: 'Servicios y Planes', icon: DocumentTextIcon },
		{ id: 'integrations', name: 'Integraciones', icon: Cog6ToothIcon }
	];

	// Mostrar loading solo si está cargando y no hay datos cargados
	if (loading && loadingForms && loadingPlanPrices) {
		return (
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center min-h-screen">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
					<p className="text-gray-600">Cargando...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
			{/* Header */}
			<div className="mb-8">
				<h1 className="text-3xl font-bold text-gray-900">Configuración del Sistema</h1>
				<p className="mt-2 text-sm text-gray-600">
					Configura las opciones del sistema y servicios externos
				</p>
			</div>

			{/* Success Message */}
			{success && (
				<div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
					<CheckCircleIcon className="h-5 w-5 text-green-600" />
					<p className="text-green-800">Configuración guardada exitosamente</p>
				</div>
			)}

			{/* Error */}
			{error && (
				<div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
					<p className="text-red-800">{error}</p>
				</div>
			)}

			{/* Pestañas */}
			<div className="mb-6 border-b border-gray-200">
				<nav className="-mb-px flex space-x-8" aria-label="Tabs">
					{tabs.map((tab) => {
						const Icon = tab.icon;
						const isActive = activeTab === tab.id;
						return (
							<button
								key={tab.id}
								onClick={() => setActiveTab(tab.id)}
								className={`
									group inline-flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
									${isActive
										? 'text-gray-900'
										: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
									}
								`}
								style={isActive ? { borderBottomColor: '#12284C', color: '#12284C' } : {}}
							>
								<Icon 
									className={`h-5 w-5 transition-colors ${isActive ? '' : 'text-gray-400 group-hover:text-gray-500'}`}
									style={isActive ? { color: '#12284C' } : {}}
								/>
								{tab.name}
							</button>
						);
					})}
				</nav>
			</div>

			{/* Contenido de las pestañas */}
			<div className="space-y-6">
				{/* Pestaña: Servicios y Planes */}
				{activeTab === 'services' && (
					<div className="space-y-6">
						{/* Links de Formularios */}
						<div className="bg-white rounded-lg shadow p-6">
							<div className="flex items-center gap-3 mb-4">
								<LinkIcon className="h-6 w-6" style={{ color: '#12284C' }} />
								<h2 className="text-xl font-semibold text-gray-900">Links de Formularios de Servicios</h2>
							</div>
							<p className="text-sm text-gray-600 mb-4">
								Configura la acción que se ejecutará al hacer clic en "Adquirir Plan" de cada servicio. Puedes elegir entre: Formulario, Red Social o WhatsApp.
							</p>
							{loadingForms ? (
								<div className="text-center py-4 text-gray-500">Cargando configuración...</div>
							) : (
								<form onSubmit={handleSaveServiceForms} className="space-y-4">
									{Object.entries({
										form_url_constitucion: 'Constitución de Empresas & ONG',
										form_url_srl_eirl: 'SRL y EIRL',
										form_url_persona_fisica: 'Persona Física',
										form_url_ong: 'ONG/Fundaciones',
										form_url_empresas_carpeta: 'Empresas en Carpeta',
										form_url_outsourcing_igualas: 'Outsourcing Contable y Fiscal',
										form_url_gestiones_legales: 'Gestiones Legales Empresariales',
										form_url_renovacion_mercantil: 'Renovación del Registro Mercantil',
										form_url_asesoria_fiscal: 'Asesoría Fiscal',
										form_url_estados_financieros: 'Estados Financieros',
										form_url_auditorias: 'Auditorías',
										form_url_inventarios: 'Inventarios',
										form_url_imagen_corporativa: 'Imagen Corporativa',
										form_url_diseno_logo: 'Diseño de Logo Empresarial',
										form_url_elaboracion_sellos: 'Elaboración de Sellos',
										form_url_asesoria_financiera: 'Asesoría Financiera',
										form_url_redes_sociales: 'Manejo de Redes Sociales',
										form_url_pautas_tv: 'Pautas Publicitarias en TV',
										form_url_posicionamiento_marca: 'Posicionamiento de Marca',
										form_url_centros_salud: 'Habilitación de Centros de Salud',
										form_url_registro_marca: 'Registro de Nombre o Marca Comercial',
										form_url_facturacion_electronica: 'Facturación Electrónica',
										form_url_general: 'General (por defecto)'
									}).map(([key, label]) => (
										<div key={key} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
											<label className="block text-sm font-medium text-gray-700 mb-2">
												{label}
											</label>
											<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
												<div>
													<label className="block text-xs font-medium text-gray-600 mb-1">
														Tipo de Acción
													</label>
													<select
														value={serviceForms[key]?.type || 'form'}
														onChange={(e) => setServiceForms({
															...serviceForms,
															[key]: { ...serviceForms[key], type: e.target.value, value: '' }
														})}
														className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0 text-sm"
														style={{ focusRingColor: '#12284C' }}
													>
														<option value="form">Formulario</option>
														<option value="social">Red Social</option>
														<option value="whatsapp">WhatsApp</option>
													</select>
												</div>
												<div className="md:col-span-2">
													<label className="block text-xs font-medium text-gray-600 mb-1">
														{serviceForms[key]?.type === 'whatsapp' ? 'Número de WhatsApp' : 
														 serviceForms[key]?.type === 'social' ? 'URL de Red Social' : 
														 'URL del Formulario'}
													</label>
													{serviceForms[key]?.type === 'whatsapp' ? (
														<input
															type="tel"
															value={serviceForms[key]?.value || ''}
															onChange={(e) => setServiceForms({
																...serviceForms,
																[key]: { ...serviceForms[key], value: e.target.value }
															})}
															className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0 text-sm"
															style={{ focusRingColor: '#12284C' }}
															placeholder="18496079129 (sin + ni espacios)"
														/>
													) : (
														<input
															type="url"
															value={serviceForms[key]?.value || ''}
															onChange={(e) => setServiceForms({
																...serviceForms,
																[key]: { ...serviceForms[key], value: e.target.value }
															})}
															className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0 text-sm"
															style={{ focusRingColor: '#12284C' }}
															placeholder={serviceForms[key]?.type === 'social' ? 
																'https://www.facebook.com/... o https://instagram.com/...' : 
																'https://forms.office.com/r/...'}
														/>
													)}
												</div>
											</div>
										</div>
									))}
									<div className="flex justify-end pt-4 border-t border-gray-200">
										<button
											type="submit"
											disabled={savingForms}
											className="px-4 py-2 rounded-lg text-white transition-opacity disabled:opacity-50"
											style={{ backgroundColor: '#12284C' }}
										>
											{savingForms ? 'Guardando...' : 'Guardar Links de Formularios'}
										</button>
									</div>
								</form>
							)}
						</div>

						{/* Precios de Planes */}
						<div className="bg-white rounded-lg shadow p-6">
							<div className="flex items-center gap-3 mb-4">
								<CurrencyDollarIcon className="h-6 w-6" style={{ color: '#12284C' }} />
								<h2 className="text-xl font-semibold text-gray-900">Precios y Avances de Planes</h2>
							</div>
							<p className="text-sm text-gray-600 mb-4">
								Configura los precios, avances y moneda para todos los planes de servicios.
							</p>
							{loadingPlanPrices ? (
								<div className="text-center py-4 text-gray-500">Cargando precios...</div>
							) : (
								<form onSubmit={handleSavePlanPrices} className="space-y-6">
									{/* Moneda */}
									<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
										<label className="block text-sm font-medium text-gray-700 mb-2">
											Moneda Global
										</label>
										<input
											type="text"
											value={planPrices.currency || 'RD$'}
											onChange={(e) => setPlanPrices({ ...planPrices, currency: e.target.value })}
											className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
											style={{ focusRingColor: '#12284C' }}
											placeholder="RD$, USD$, etc."
										/>
										<p className="mt-2 text-xs text-gray-500">
											Esta moneda se aplicará a todos los planes
										</p>
									</div>

									{/* Precios por tipo de plan */}
									<div className="space-y-4">
										{Object.entries({
											ong: 'ONG/Fundaciones',
											srl: 'SRL',
											eirl: 'EIRL',
											persona_fisica: 'Persona Física'
										}).map(([planType, label]) => (
											<div key={planType} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
												<h3 className="font-semibold text-gray-900 mb-4 text-lg">{label}</h3>
												<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
													{Object.entries({
														basico: 'Básico',
														premium: 'Premium',
														ultra: 'Ultra',
														elite: planType === 'persona_fisica' ? null : 'Elite'
													}).filter(([_, name]) => name !== null).map(([planKey, planName]) => (
														<div key={planKey} className="bg-white rounded-lg p-4 border border-gray-300 shadow-sm">
															<h4 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">{planName}</h4>
															<div className="space-y-3">
																<div>
																	<label className="block text-xs font-medium text-gray-700 mb-1">Precio</label>
																	<input
																		type="number"
																		value={planPrices[planType]?.[planKey]?.price || ''}
																		onChange={(e) => updatePlanPrice(planType, planKey, 'price', e.target.value)}
																		className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
																		style={{ focusRingColor: '#12284C' }}
																		placeholder="0"
																	/>
																</div>
																<div>
																	<label className="block text-xs font-medium text-gray-700 mb-1">Avance</label>
																	<input
																		type="number"
																		value={planPrices[planType]?.[planKey]?.advance || ''}
																		onChange={(e) => updatePlanPrice(planType, planKey, 'advance', e.target.value)}
																		className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
																		style={{ focusRingColor: '#12284C' }}
																		placeholder="0"
																	/>
																</div>
															</div>
														</div>
													))}
												</div>
											</div>
										))}
									</div>

									<div className="flex justify-end pt-4 border-t border-gray-200">
										<button
											type="submit"
											disabled={savingPlanPrices}
											className="px-4 py-2 rounded-lg text-white transition-opacity disabled:opacity-50"
											style={{ backgroundColor: '#12284C' }}
										>
											{savingPlanPrices ? 'Guardando...' : 'Guardar Precios de Planes'}
										</button>
									</div>
								</form>
							)}
						</div>
					</div>
				)}

				{/* Pestaña: Integraciones */}
				{activeTab === 'integrations' && (
					<form onSubmit={handleSave} className="space-y-6">
				{/* Configuración de Email */}
				<div className="bg-white rounded-lg shadow p-6">
					<div className="flex items-center gap-3 mb-4">
						<EnvelopeIcon className="h-6 w-6" style={{ color: '#12284C' }} />
						<h2 className="text-xl font-semibold text-gray-900">Configuración de Email</h2>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Servidor SMTP
							</label>
							<input
								type="text"
								value={settings.email_host}
								onChange={(e) => setSettings({ ...settings, email_host: e.target.value })}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
								style={{ focusRingColor: '#12284C' }}
								placeholder="smtp.gmail.com"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Puerto
							</label>
							<input
								type="number"
								value={settings.email_port}
								onChange={(e) => setSettings({ ...settings, email_port: e.target.value })}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
								style={{ focusRingColor: '#12284C' }}
								placeholder="587"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Usuario
							</label>
							<input
								type="text"
								value={settings.email_host_user}
								onChange={(e) => setSettings({ ...settings, email_host_user: e.target.value })}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
								style={{ focusRingColor: '#12284C' }}
								placeholder="usuario@ejemplo.com"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Contraseña
							</label>
							<input
								type="password"
								value={settings.email_host_password}
								onChange={(e) => setSettings({ ...settings, email_host_password: e.target.value })}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
								style={{ focusRingColor: '#12284C' }}
							/>
						</div>
						<div className="md:col-span-2">
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Email Remitente
							</label>
							<input
								type="email"
								value={settings.default_from_email}
								onChange={(e) => setSettings({ ...settings, default_from_email: e.target.value })}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
								style={{ focusRingColor: '#12284C' }}
								placeholder="no-reply@psasesoresconsultores.com"
							/>
						</div>
						<div>
							<label className="flex items-center space-x-2">
								<input
									type="checkbox"
									checked={settings.email_use_tls}
									onChange={(e) => setSettings({ ...settings, email_use_tls: e.target.checked })}
									className="rounded border-gray-300"
									style={{ accentColor: '#12284C' }}
								/>
								<span className="text-sm font-medium text-gray-700">Usar TLS</span>
							</label>
						</div>
					</div>
					<div className="mt-4 pt-4 border-t border-gray-200">
						<button
							type="submit"
							disabled={saving}
							className="px-4 py-2 rounded-lg text-white transition-opacity disabled:opacity-50"
							style={{ backgroundColor: '#12284C' }}
						>
							{saving ? 'Guardando...' : 'Guardar Configuración de Email'}
						</button>
					</div>
				</div>

				{/* Configuración de n8n */}
				<div className="bg-white rounded-lg shadow p-6">
					<div className="flex items-center gap-3 mb-4">
						<GlobeAltIcon className="h-6 w-6" style={{ color: '#12284C' }} />
						<h2 className="text-xl font-semibold text-gray-900">Configuración de n8n</h2>
					</div>
					<div className="space-y-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								URL del Webhook
							</label>
							<input
								type="url"
								value={settings.n8n_webhook_url}
								onChange={(e) => setSettings({ ...settings, n8n_webhook_url: e.target.value })}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
								style={{ focusRingColor: '#12284C' }}
								placeholder="https://tu-n8n.com/webhook/..."
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Token de API
							</label>
							<input
								type="password"
								value={settings.n8n_api_token}
								onChange={(e) => setSettings({ ...settings, n8n_api_token: e.target.value })}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
								style={{ focusRingColor: '#12284C' }}
								placeholder="Token de autenticación"
							/>
						</div>
					</div>
					<div className="mt-4 pt-4 border-t border-gray-200">
						<button
							type="submit"
							disabled={saving}
							className="px-4 py-2 rounded-lg text-white transition-opacity disabled:opacity-50"
							style={{ backgroundColor: '#12284C' }}
						>
							{saving ? 'Guardando...' : 'Guardar Configuración de n8n'}
						</button>
					</div>
				</div>

				{/* Configuración de Google Vision API */}
				<div className="bg-white rounded-lg shadow p-6">
					<div className="flex items-center gap-3 mb-4">
						<CloudIcon className="h-6 w-6" style={{ color: '#12284C' }} />
						<h2 className="text-xl font-semibold text-gray-900">Google Vision API</h2>
					</div>
					<div className="space-y-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Archivo JSON de Credenciales
							</label>
							<input
								type="file"
								accept=".json"
								onChange={handleGoogleVisionFileChange}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
								style={{ focusRingColor: '#12284C' }}
							/>
							<p className="mt-1 text-xs text-gray-500">
								Sube el archivo JSON de credenciales de Google Cloud Vision API
							</p>
						</div>

						{googleVisionFilePreview && (
							<div className="mt-4">
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Vista Previa del JSON
								</label>
								<div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-64 overflow-y-auto">
									<pre className="text-xs font-mono text-gray-700 whitespace-pre-wrap">
										{googleVisionFilePreview}
									</pre>
								</div>
							</div>
						)}

						{googleVisionFile && (
							<button
								type="button"
								onClick={handleUploadGoogleVision}
								disabled={saving}
								className="px-4 py-2 rounded-lg text-white transition-opacity disabled:opacity-50"
								style={{ backgroundColor: '#12284C' }}
							>
								{saving ? 'Subiendo...' : 'Subir Credenciales'}
							</button>
						)}

						{googleVisionFilePreview && !googleVisionFile && (
							<div className="mt-2">
								<p className="text-sm text-green-600">✓ Credenciales configuradas</p>
								<a
									href={googleVisionFilePreview}
									target="_blank"
									rel="noopener noreferrer"
									className="text-sm text-blue-600 hover:underline"
								>
									Ver archivo actual
								</a>
							</div>
						)}
					</div>
				</div>

				{/* ────────────────────────────────────────
					SECCIÓN: Procesamiento IA (LLM Directo)
				──────────────────────────────────────── */}
				<div id="llm-procesamiento" className="bg-white rounded-lg shadow p-6 scroll-mt-6">
					<div className="flex items-center justify-between mb-4">
						<div className="flex items-center gap-3">
							<CpuChipIcon className="h-6 w-6" style={{ color: '#12284C' }} />
							<h2 className="text-xl font-semibold text-gray-900">Procesamiento con IA</h2>
						</div>
						<span className={`px-3 py-1 rounded-full text-xs font-semibold ${
							llmConfig.llm_modo_procesamiento === 'llm'
								? 'bg-green-100 text-green-700'
								: 'bg-gray-100 text-gray-600'
						}`}>
							{llmConfig.llm_modo_procesamiento === 'llm' ? '● LLM Directo activo' : '● Usando n8n'}
						</span>
					</div>

					{/* ── Panel de configuración activa – siempre visible ── */}
					{!loadingLlm && (
						<div className={`rounded-lg border p-4 mb-5 ${
							llmConfig.llm_modo_procesamiento === 'llm'
								? 'bg-blue-50 border-blue-200'
								: 'bg-gray-50 border-gray-200'
						}`}>
							<p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Configuración activa</p>
							<div className="flex flex-wrap gap-4 text-sm">
								<div className="flex items-center gap-1.5">
									<span className="text-gray-400">Modo:</span>
									<span className={`font-semibold ${llmConfig.llm_modo_procesamiento === 'llm' ? 'text-blue-700' : 'text-gray-700'}`}>
										{llmConfig.llm_modo_procesamiento === 'llm' ? '🤖 LLM Directo' : '🔗 n8n'}
									</span>
								</div>
								{llmConfig.llm_modo_procesamiento === 'llm' && (
								<>
									<div className="flex items-center gap-1.5">
										<span className="text-gray-400">Proveedor:</span>
										<span className="font-semibold text-gray-700">
											{LLM_PROVIDERS.find(p => p.value === activeProvider)?.label || activeProvider}
										</span>
									</div>
									<div className="flex items-center gap-1.5">
										<span className="text-gray-400">Modelo:</span>
										<span className="font-semibold text-gray-700">
											{LLM_PROVIDERS.find(p => p.value === activeProvider)
												?.models.find(m => m.id === activeProviderCfg.model)?.label
												|| activeProviderCfg.model}
										</span>
									</div>
									<div className="flex items-center gap-1.5">
										<span className="text-gray-400">API Key:</span>
										<span className={`font-semibold ${activeProviderCfg.api_key ? 'text-green-600' : 'text-red-500'}`}>
											{activeProviderCfg.api_key ? '✓ Configurada' : '✗ No configurada'}
										</span>
									</div>
								</>
							)}
							</div>
						</div>
					)}

					<p className="text-sm text-gray-500 mb-5">
						Elige cómo se procesarán las facturas después del OCR. <strong>n8n</strong> usa tu flujo actual;
						<strong> LLM Directo</strong> llama al modelo de IA sin intermediarios (más rápido y económico).
					</p>

					{loadingLlm ? (
						<div className="text-center py-6 text-gray-400">Cargando configuración...</div>
					) : (
						<div className="space-y-6">

							{/* — Modo — */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">Modo de Procesamiento</label>
								<div className="flex gap-4">
									{[
										{ value: 'n8n',  label: '🔗 n8n (flujo actual)',     desc: 'Usa el webhook de n8n configurado' },
										{ value: 'llm',  label: '🤖 LLM Directo (nuevo)',    desc: 'Llama al modelo de IA sin pasar por n8n' },
									].map(opt => (
										<label
											key={opt.value}
											className={`flex-1 border-2 rounded-lg p-4 cursor-pointer transition-all ${
												llmConfig.llm_modo_procesamiento === opt.value
													? 'border-blue-500 bg-blue-50'
													: 'border-gray-200 hover:border-gray-300'
											}`}
										>
											<input
												type="radio"
												name="modo_procesamiento"
												value={opt.value}
												checked={llmConfig.llm_modo_procesamiento === opt.value}
								onChange={e => {
										setLlmConfig(prev => ({ ...prev, llm_modo_procesamiento: e.target.value }));
										setLlmPendingChanges(true);
									}}
								className="sr-only"
											/>
											<div className="font-semibold text-gray-800 text-sm">{opt.label}</div>
											<div className="text-xs text-gray-500 mt-1">{opt.desc}</div>
										</label>
									))}
								</div>
							</div>

						{/* — Proveedor y Modelo — */}
						<div className="grid grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Proveedor LLM</label>
								<select
									value={llmConfig.llm_provider}
									onChange={e => {
										const newProvider = e.target.value;
										// Solo cambiar el proveedor activo; el modelo/key ya están en llmProviderConfigs
										setLlmConfig(prev => ({ ...prev, llm_provider: newProvider }));
										setLlmTestResult(null);
										setLlmPendingChanges(true);
									}}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
								>
									{LLM_PROVIDERS.map(p => (
										<option key={p.value} value={p.value}>{p.label}</option>
									))}
								</select>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Modelo</label>
								<select
									value={activeProviderCfg.model}
									onChange={e => {
										const newModel = e.target.value;
										setActiveProviderField('model', newModel);
										autoSaveModel(activeProvider, newModel);
									}}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
								>
									{(LLM_PROVIDERS.find(p => p.value === activeProvider)?.models || []).map(m => (
										<option key={m.id} value={m.id}>{m.label}</option>
									))}
								</select>
								<p className="text-xs text-gray-400 mt-1">También puedes escribir un modelo personalizado</p>
								<input
									type="text"
									value={activeProviderCfg.model}
									onChange={e => {
										setActiveProviderField('model', e.target.value);
									}}
									onBlur={e => autoSaveModel(activeProvider, e.target.value)}
									className="mt-1 w-full px-3 py-1.5 border border-gray-200 rounded text-xs text-gray-600"
									placeholder="o escribe el nombre del modelo..."
								/>
							</div>
						</div>

						{/* — API Key — */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								API Key <span className="text-gray-400 font-normal">(se guarda por proveedor)</span>
							</label>
							<div className="flex gap-2">
								<input
									type="password"
									value={activeProviderCfg.api_key}
									onChange={e => {
										setActiveProviderField('api_key', e.target.value);
										setActiveProviderField('apiKeyChanged', true);
									}}
									placeholder={activeProviderCfg.api_key ? '••••••••••••••••' : 'sk-... / sk-ant-... / AIza...'}
									className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
								/>
									<button
										type="button"
										onClick={handleTestLlm}
										disabled={testingLlm}
										className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
									>
										{testingLlm ? (
											<>
												<ArrowPathIcon className="h-4 w-4 animate-spin" />
												Probando...
											</>
										) : (
											<>
												<BeakerIcon className="h-4 w-4" />
												Probar
											</>
										)}
									</button>
								</div>

								{/* Resultado del test */}
								{llmTestResult && (
									<div className={`mt-2 p-3 rounded-lg text-sm ${
										llmTestResult.success
											? 'bg-green-50 border border-green-200 text-green-800'
											: 'bg-red-50 border border-red-200 text-red-800'
									}`}>
										{llmTestResult.success ? (
											<div>
												<div className="font-semibold">✅ {llmTestResult.message}</div>
												<div className="mt-1 text-xs">
													Campos extraídos: <span className="font-mono">{llmTestResult.campos_extraidos?.join(', ')}</span>
												</div>
											</div>
										) : (
											<div className="flex items-start gap-2">
												<ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
												<span>{llmTestResult.error}</span>
											</div>
										)}
									</div>
								)}
							</div>

							{/* — Prompts — */}
							<div>
								<div className="flex items-center justify-between mb-2">
									<label className="block text-sm font-medium text-gray-700">Prompts de Extracción</label>
									<button
										type="button"
										onClick={loadDefaultPrompts}
										className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
									>
										<ArrowPathIcon className="h-3 w-3" />
										Restaurar prompts por defecto
									</button>
								</div>
								<p className="text-xs text-gray-500 mb-3">
									Usa <code className="bg-gray-100 px-1 rounded">{'{texto_ocr}'}</code>,{' '}
									<code className="bg-gray-100 px-1 rounded">{'{rnc_empresa_origen}'}</code> y{' '}
									<code className="bg-gray-100 px-1 rounded">{'{actividad_economica}'}</code> como variables.
								</p>

								{/* Tabs 606 / 607 */}
								<div className="flex gap-2 mb-2">
									{['606', '607'].map(t => (
										<button
											key={t}
											type="button"
											onClick={() => setActivePromptTab(t)}
											className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
												activePromptTab === t
													? 'text-white'
													: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
											}`}
											style={activePromptTab === t ? { backgroundColor: '#12284C' } : {}}
										>
											Formulario {t}
										</button>
									))}
								</div>

								{activePromptTab === '606' && (
									<textarea
										value={llmConfig.llm_prompt_606}
										onChange={e => setLlmConfig(prev => ({ ...prev, llm_prompt_606: e.target.value }))}
										rows={14}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono focus:ring-2 focus:outline-none resize-y"
										placeholder="Escribe el prompt para el formulario 606..."
									/>
								)}
								{activePromptTab === '607' && (
									<textarea
										value={llmConfig.llm_prompt_607}
										onChange={e => setLlmConfig(prev => ({ ...prev, llm_prompt_607: e.target.value }))}
										rows={14}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono focus:ring-2 focus:outline-none resize-y"
										placeholder="Escribe el prompt para el formulario 607..."
									/>
								)}
							</div>

						{/* — Mensajes — */}
						{llmError && (
							<div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
								{llmError}
							</div>
						)}
						{llmSuccess && (
							<div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2 text-sm text-green-800">
								<CheckCircleIcon className="h-4 w-4" />
								Configuración LLM guardada exitosamente
							</div>
						)}

						{/* — Advertencia cambios pendientes — */}
						{llmPendingChanges && !llmSuccess && (
							<div className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-center gap-2 text-sm text-amber-800">
								<ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
								<span>Tienes cambios sin guardar. El modelo y la API key se aplicarán al procesar facturas solo después de guardar.</span>
							</div>
						)}

						{/* — Botón guardar — */}
						<div className="pt-2 border-t border-gray-100">
							<button
								type="button"
								onClick={handleSaveLlm}
								disabled={savingLlm}
								className={`px-5 py-2 rounded-lg text-white font-medium transition-all disabled:opacity-50 ${llmPendingChanges ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}
								style={{ backgroundColor: '#12284C' }}
							>
								{savingLlm ? 'Guardando...' : llmPendingChanges ? '💾 Guardar Cambios Pendientes' : 'Guardar Configuración LLM'}
							</button>
						</div>

						</div>
					)}
				</div>
					</form>
				)}
			</div>
		</div>
	);
};

export default SystemSettings;

